import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Search, CheckSquare, Square, AlertCircle, Plane, Ship, Check } from 'lucide-react';

const parseNum = (val, isFloat = true) => {
  if (val === null || val === undefined || val === '') return 0;
  if (typeof val === 'number') return isNaN(val) ? 0 : val;
  const cleaned = String(val).replace(/,/g, '').match(/[-+]?(?:\d+(?:\.\d+)?|\.\d+)/);
  if (!cleaned) return 0;
  const num = isFloat ? parseFloat(cleaned[0]) : parseInt(cleaned[0], 10);
  return isNaN(num) ? 0 : num;
};

export const HouseDocketConsolidationPicker = ({
  isOpen,
  onClose,
  companyId,
  currentDocketId,
  shipmentType = 'AIR',
  selectedDocketIds = [],
  onConfirm
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dockets, setDockets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set(selectedDocketIds || []));
  const isSea = (shipmentType || '').toUpperCase() === 'SEA';

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Synchronize internal state whenever modal opens or selectedDocketIds changes
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set(selectedDocketIds || []));
      fetchEligibleDockets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, companyId, currentDocketId]);

  const fetchEligibleDockets = async () => {
    if (!companyId) {
      setDockets([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams({
        company_id: companyId,
        shipment_type: shipmentType || 'AIR',
      });
      if (currentDocketId) {
        params.append('current_docket_id', currentDocketId);
      }

      const res = await fetch(`${backendUrl}/api/dockets/eligible-consolidation-items?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) {
        throw new Error('Failed to load eligible dockets for consolidation');
      }

      const data = await res.json();
      setDockets(data || []);
    } catch (err) {
      console.error('Error fetching eligible consolidation dockets:', err);
      setError(err.message || 'Could not load dockets');
    } finally {
      setLoading(false);
    }
  };

  const filteredDockets = useMemo(() => {
    if (!searchTerm.trim()) return dockets;
    const term = searchTerm.toLowerCase();
    return dockets.filter(d =>
      (d.system_docket_no || '').toLowerCase().includes(term) ||
      (d.printed_docket_no || '').toLowerCase().includes(term) ||
      (d.hawb_no || '').toLowerCase().includes(term) ||
      (d.shipper_name || '').toLowerCase().includes(term) ||
      (d.consignee_name || '').toLowerCase().includes(term) ||
      (d.destination || '').toLowerCase().includes(term)
    );
  }, [dockets, searchTerm]);

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const handleSelectAllFiltered = () => {
    const next = new Set(selectedIds);
    const allFilteredSelected = filteredDockets.length > 0 && filteredDockets.every(d => next.has(d.id));
    if (allFilteredSelected) {
      filteredDockets.forEach(d => next.delete(d.id));
    } else {
      filteredDockets.forEach(d => next.add(d.id));
    }
    setSelectedIds(next);
  };

  // Selected totals computation
  const selectedSummary = useMemo(() => {
    const selectedList = dockets.filter(d => selectedIds.has(d.id));
    const totalPkgs = selectedList.reduce((acc, d) => acc + parseNum(d.packages, false), 0);
    const totalWeight = selectedList.reduce((acc, d) => acc + parseNum(d.gross_weight, true), 0);
    return {
      count: selectedList.length,
      packages: totalPkgs,
      grossWeight: Math.round(totalWeight * 100) / 100,
      items: selectedList
    };
  }, [dockets, selectedIds]);

  const handleConfirm = () => {
    onConfirm(selectedSummary.items);
    onClose();
  };

  const allFilteredSelected = filteredDockets.length > 0 && filteredDockets.every(d => selectedIds.has(d.id));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0 overflow-hidden font-ibm">
        <DialogHeader className="p-6 pb-4 border-b border-gray-100 bg-sky-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-sky-500 rounded-lg text-white">
              {isSea ? <Ship className="w-5 h-5" /> : <Plane className="w-5 h-5" />}
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-gray-900 font-manrope">
                Select House Dockets ({isSea ? 'HBL' : 'HAWB'}) for Consolidation
              </DialogTitle>
              <p className="text-xs text-gray-500 mt-0.5">
                Attach unconsolidated House shipments into this Master {isSea ? 'Sea' : 'Air'} Consolidation job.
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Search bar & controls */}
        <div className="p-4 px-6 bg-white border-b border-gray-100 flex items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder={`Search by Docket #, ${isSea ? 'HBL' : 'HAWB'} #, Shipper, Consignee...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-9 text-xs"
              data-testid="consol-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSelectAllFiltered}
              className="h-8 text-xs font-ibm"
              disabled={filteredDockets.length === 0}
            >
              {allFilteredSelected ? (
                <>
                  <CheckSquare className="w-3.5 h-3.5 mr-1.5 text-sky-600" /> Deselect All
                </>
              ) : (
                <>
                  <Square className="w-3.5 h-3.5 mr-1.5 text-gray-400" /> Select All Filtered
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Dockets Table / List */}
        <div className="flex-1 overflow-y-auto p-6 pt-2">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-500">
              <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
              <p className="text-sm">Loading eligible house dockets...</p>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12 text-red-600 gap-2">
              <AlertCircle className="w-5 h-5" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          ) : filteredDockets.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Plane className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm font-medium text-gray-600">No eligible House dockets found</p>
              <p className="text-xs text-gray-400 mt-1">
                {searchTerm ? 'Try adjusting your search criteria.' : 'Create individual AIR dockets first, then attach them here.'}
              </p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs text-left">
                <thead className="bg-gray-50/80 text-gray-600 uppercase text-[11px] font-semibold border-b border-gray-200">
                  <tr>
                    <th className="p-3 w-10 text-center">Select</th>
                    <th className="p-3">Docket No</th>
                    <th className="p-3">{isSea ? 'HBL No' : 'HAWB No'}</th>
                    <th className="p-3">Shipper (Consignor)</th>
                    <th className="p-3">Consignee</th>
                    <th className="p-3 text-right">Packages</th>
                    <th className="p-3 text-right">Gross Wt (kg)</th>
                    <th className="p-3">Destination</th>
                    <th className="p-3 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredDockets.map((d) => {
                    const isChecked = selectedIds.has(d.id);
                    return (
                      <tr
                        key={d.id}
                        onClick={() => toggleSelect(d.id)}
                        className={`cursor-pointer transition-colors hover:bg-sky-50/60 ${
                          isChecked ? 'bg-sky-50/80 font-medium' : ''
                        }`}
                        data-testid={`consol-row-${d.id}`}
                      >
                        <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSelect(d.id)}
                            className="w-4 h-4 rounded text-sky-600 focus:ring-sky-500 cursor-pointer"
                            data-testid={`consol-checkbox-${d.id}`}
                          />
                        </td>
                        <td className="p-3 font-semibold text-gray-900">
                          {d.system_docket_no}
                          {d.printed_docket_no ? (
                            <span className="block text-[10px] text-gray-400 font-normal">
                              Book: {d.printed_docket_no}
                            </span>
                          ) : null}
                        </td>
                        <td className="p-3">
                          <span className="inline-block px-2 py-0.5 rounded bg-violet-100 text-violet-700 font-semibold text-[11px]">
                            {d.hawb_no || '-'}
                          </span>
                        </td>
                        <td className="p-3 text-gray-700 max-w-[150px] truncate" title={d.shipper_name}>
                          {d.shipper_name || '-'}
                        </td>
                        <td className="p-3 text-gray-700 max-w-[150px] truncate" title={d.consignee_name}>
                          {d.consignee_name || '-'}
                        </td>
                        <td className="p-3 text-right font-semibold text-gray-900">
                          {parseNum(d.packages, false) || 0}
                        </td>
                        <td className="p-3 text-right font-semibold text-gray-900">
                          {parseNum(d.gross_weight, true).toFixed(2)}
                        </td>
                        <td className="p-3 text-gray-600">
                          {d.destination || '-'}
                        </td>
                        <td className="p-3 text-center">
                          <span className="px-2 py-0.5 text-[10px] rounded-full bg-blue-50 text-blue-600 font-semibold">
                            {d.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer with totals summary and confirm */}
        <DialogFooter className="p-4 px-6 border-t border-gray-100 bg-gray-50 flex items-center justify-between sm:justify-between w-full">
          <div className="flex items-center gap-4 text-xs font-ibm">
            <div className="bg-white px-3 py-1.5 rounded-md border border-gray-200">
              <span className="text-gray-500">Selected Dockets:</span>{' '}
              <strong className="text-sky-600 font-bold">{selectedSummary.count}</strong>
            </div>
            <div className="bg-white px-3 py-1.5 rounded-md border border-gray-200">
              <span className="text-gray-500">Total Packages:</span>{' '}
              <strong className="text-emerald-600 font-bold">{selectedSummary.packages}</strong>
            </div>
            <div className="bg-white px-3 py-1.5 rounded-md border border-gray-200">
              <span className="text-gray-500">Total Gross Weight:</span>{' '}
              <strong className="text-amber-600 font-bold">{selectedSummary.grossWeight.toFixed(2)} kg</strong>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} className="h-9">
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleConfirm}
              className="h-9 bg-sky-600 hover:bg-sky-700 text-white font-medium"
              data-testid="consol-confirm-btn"
            >
              <Check className="w-4 h-4 mr-1.5" />
              Attach Selected ({selectedSummary.count})
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
